import { useTheme } from '@emotion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { RESET, useAtomCallback } from 'jotai/utils';
import React, { useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useIntercom } from 'react-use-intercom';

import { Dialog, Flex, Grid, Text } from '~/components/blocks';
import { EditMobilePhoneModal } from '~/components/partials/PatientDetailDialog/EditMobilePhoneModal';
import { usePlanFeature } from '~/hooks/use-plan-feature';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import {
  eventPageInfoState,
  patientDetailConfirmBreakawayState,
  patientDetailDialogMessageState,
  patientDetailDialogProfileState,
  patientDetailDialogState,
  patientDetailDialogTracingReportState,
} from '~/state/partials/patient_detail_dialog/atoms';
import { PatientDetailDiaLogActionType } from '~/state/partials/patient_detail_dialog/types';

import { ConfirmBreakawayModal } from './ConfirmBreakawayModal';
import { DeleteFollowupCommentModal } from './DeleteFollowupCommentModal';
import { DeleteMedicationFollowupMessageModal } from './DeleteMedicationFollowupMessageModal';
import { DialogHeder } from './DialogHeder';
import { EventPane } from './EventPane';
import { NewMedicationFollowupMessagePane } from './NewMedicationFollowupMessagePane';
import { useFetchPatientProfile } from './NewMedicationFollowupMessagePane/use-fetch-patient';
import { NewTracingReportPane } from './NewTracingReportPane';
import { PatientPane } from './PatientPane';
import { useFetchPatient } from './use-fetch-patient';
import { useMedicationFollowupSettingStatus } from './use-medication-followup-setting-status';

export const PatientDetailDialog = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery({ query: theme.mediaQueries.tablet });
  const { isOpen } = useAtomValue(patientDetailDialogState);
  const { hasEnabledFeature: hasEnabledFollowup, loading } = usePlanFeature('followup');
  const setProfileState = useSetAtom(patientDetailDialogMessageState);
  const { patient } = useFetchPatient();
  const { profile } = useFetchPatientProfile();
  const { statusManagement } = useMedicationFollowupSettingStatus();
  const { hide: closeIntercom } = useIntercom();

  const { verifyRole } = usePractitionerRole();
  const isVerified = verifyRole('pharmacist');

  const handleClose = useAtomCallback(
    useCallback((get, set) => {
      const { selectedMessageId } = get(patientDetailDialogMessageState);

      if (selectedMessageId) {
        set(patientDetailConfirmBreakawayState, (_state) => ({
          ..._state,
          isOpen: true,
          action: 'close' as PatientDetailDiaLogActionType,
        }));
      } else {
        set(patientDetailDialogState, RESET);
        set(patientDetailDialogMessageState, RESET);
        set(patientDetailDialogTracingReportState, RESET);
        set(eventPageInfoState, RESET);
        set(patientDetailDialogProfileState, RESET);
        set(patientDetailConfirmBreakawayState, RESET);
      }
    }, []),
  );

  useEffect(() => {
    if (profile) {
      setProfileState((_state) => ({ ..._state, profile }));
    }
    if (isOpen) {
      closeIntercom();
    }
  }, [closeIntercom, isOpen, profile, setProfileState]);

  if (loading) return null;
  if (!hasEnabledFollowup) {
    return (
      <Dialog open={isOpen} onClose={handleClose}>
        <Dialog.Body>
          <Flex
            width="100%"
            height="100%"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            padding={theme.space.xl}
          >
            <Text textAlign="center" fontWeight="bold" size="xl" block>
              服薬フォローアップ機能は
              <br />
              現在ご利用いただけません
            </Text>
          </Flex>
        </Dialog.Body>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogHeder patient={patient} statusManagement={statusManagement} />
      <Dialog.Body>
        <Grid
          height="100%"
          gridTemplateColumns={`${
            isVerified ? 'min-content auto min-content' : 'auto min-content'
          }`}
          overflow="hidden"
        >
          {isVerified ? (
            <>
              <EventPane
                patient={patient}
                statusManagement={statusManagement}
                width={isTablet ? '320px' : '420px'}
                isPharmacist={isVerified}
              />
              <NewTracingReportPane patient={patient} />
              <NewMedicationFollowupMessagePane patient={patient} />
            </>
          ) : (
            <EventPane
              patient={patient}
              statusManagement={statusManagement}
              isPharmacist={isVerified}
            />
          )}
          <PatientPane
            patient={patient}
            width={isTablet ? '300px' : '360px'}
            isPharmacist={isVerified}
          />
        </Grid>
      </Dialog.Body>
      <DeleteFollowupCommentModal patient={patient} />
      <ConfirmBreakawayModal />
      <DeleteMedicationFollowupMessageModal />
      {patient?.medixsPatient && <EditMobilePhoneModal patient={patient.medixsPatient} />}
    </Dialog>
  );
};
